if (!customElements.get('pickup-availability')) {
  let drawerSequence = 0;
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends BaseElement {
      connectedCallback() {
        super.connectedCallback();
        this.errorHtml = this.errorHtml || this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
        if (this.hasAttribute('available')) this.fetchAvailability(this.getAttribute('data-variant-id'));
      }

      disconnectedCallback() {
        this.cancelRequest();
        this.removeDrawer();
        super.disconnectedCallback();
      }

      cancelRequest() {
        const previous = this.availabilityAbortController;
        this.availabilityAbortController = null;
        previous?.abort();
        clearTimeout(this.availabilityTimeout);
        this.removeAttribute('aria-busy');
      }

      removeDrawer() {
        const drawer = this.drawer;
        this.drawer = null;
        if (!drawer) return;
        if (drawer.hasAttribute('open') && typeof drawer.hide === 'function') {
          Promise.resolve(drawer.hide()).finally(() => drawer.remove());
        }
        else drawer.remove();
      }

      async fetchAvailability(variantId) {
        if (!this.isConnected) return;
        const id = String(variantId ?? '');
        if (this.availabilityAbortController && this.getAttribute('data-variant-id') === id) return;
        this.cancelRequest();
        this.removeDrawer();
        this.setAttribute('data-variant-id', id);
        if (!/^\d+$/.test(id) || Number(id) <= 0) {
          this.showEmpty();
          return;
        }
        const controller = new AbortController();
        this.availabilityAbortController = controller;
        this.availabilityTimeout = setTimeout(() => controller.abort(), 15000);
        const current = () => this.isConnected && this.availabilityAbortController === controller;
        this.hidden = false;
        this.removeAttribute('available');
        this.setAttribute('aria-busy', 'true');
        const loading = document.createElement('p');
        loading.className = 'text-sm';
        loading.setAttribute('role', 'status');
        loading.textContent = document.documentElement.lang.startsWith('tr')
          ? 'Mağazadan teslim alma bilgisi kontrol ediliyor…'
          : 'Checking store pickup availability…';
        this.replaceChildren(loading);
        try {
          let rootUrl = this.getAttribute('data-root-url') || '/';
          if (!rootUrl.endsWith('/')) rootUrl += '/';
          const response = await fetch(`${rootUrl}variants/${id}/?section_id=pickup-availability`, { signal: controller.signal });
          if (!response.ok) throw new Error('Pickup request failed');
          const html = await response.text();
          if (!current()) return;
          if (controller.signal.aborted) throw new Error('Pickup request timed out');
          const section = new DOMParser().parseFromString(html, 'text/html').getElementById('shopify-section-pickup-availability');
          if (!section) throw new Error('Missing pickup section');
          this.renderPreview(section);
        }
        catch {
          if (current()) this.renderError();
        }
        finally {
          if (this.availabilityAbortController === controller) {
            clearTimeout(this.availabilityTimeout);
            this.availabilityAbortController = null;
            this.removeAttribute('aria-busy');
          }
        }
      }

      onClickRefreshList() {
        this.fetchAvailability(this.getAttribute('data-variant-id'));
      }

      update(variant) {
        if (variant?.available) this.fetchAvailability(variant.id);
        else {
          this.cancelRequest();
          this.removeDrawer();
          this.setAttribute('data-variant-id', String(variant?.id ?? ''));
          this.showEmpty();
        }
      }

      showEmpty() {
        this.replaceChildren();
        this.removeAttribute('available');
        this.hidden = true;
      }

      renderError() {
        this.hidden = false;
        this.removeAttribute('available');
        const content = this.errorHtml?.cloneNode(true) || document.createElement('div');
        content.setAttribute('role', 'alert');
        if (!this.errorHtml) {
          const tr = document.documentElement.lang.startsWith('tr');
          const message = document.createElement('p');
          message.textContent = tr ? 'Teslim alma bilgisi yüklenemedi.' : 'Pickup availability could not be loaded.';
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = tr ? 'Yeniden dene' : 'Try again';
          content.append(message, button);
        }
        this.replaceChildren(content);
        this.querySelector('button')?.addEventListener('click', () => this.onClickRefreshList());
      }

      renderPreview(section) {
        const preview = section.querySelector('.pickup-availability-preview');
        if (!preview) {
          this.showEmpty();
          return;
        }
        const drawer = section.querySelector('.pickup-availability-drawer');
        if (!drawer) throw new Error('Missing pickup drawer');
        const oldId = drawer.id;
        drawer.id = `PickupAvailability-${++drawerSequence}`;
        for (const root of [preview, drawer]) {
          root.querySelectorAll('[aria-controls]').forEach(control => {
            if (control.getAttribute('aria-controls') === oldId) control.setAttribute('aria-controls', drawer.id);
          });
        }
        this.replaceChildren(preview);
        this.hidden = false;
        this.setAttribute('available', '');
        this.drawer = drawer;
        document.body.appendChild(drawer);
      }
    }
  );
}
